import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { clearErrors, getUserDetails, updateUser } from '../../actions/userAction';
import { UPDATE_USER_RESET, REMOVE_USER_DETAILS } from '../../constants/userConstants';
import Loading from './Loading';
import Avatar from '@mui/material/Avatar';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const UpdateUser = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const params = useParams();
    const navigate = useNavigate();

    const { user, error, loading } = useSelector((state) => state.userDetails);
    const { isUpdated, error: updateError, loading: updateLoading } = useSelector((state) => state.profile);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [gender, setGender] = useState("");
    const [hasAdminAccess, setHasAdminAccess] = useState(false);
    const [role, setRole] = useState("");
    const [adminRole, setAdminRole] = useState("");
    const [allRoles, setAllRoles] = useState([]);
    const [avatarPreview, setAvatarPreview] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [emailVerified, setEmailVerified] = useState(true);

    const userId = params.id;

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/roles');
                setAllRoles(data.roles);
            } catch (error) {
                console.error('Failed to load roles', error);
            }
        };
        fetchRoles();
    }, []);

    const updateUserSubmitHandler = (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.set("name", name);
        formData.set("email", email);
        formData.set("gender", gender);
        formData.set("role", role);
        formData.set("emailVerified", emailVerified);
        if (role === "staff") {
            formData.set("adminRole", adminRole);
        }

        dispatch(updateUser(userId, formData));
    }

    const handleSetPassword = async () => {
        if (newPassword.length < 8) {
            enqueueSnackbar("Password must be at least 8 characters", { variant: "warning" });
            return;
        }
        try {
            await axios.put(`/api/v1/admin/user/${userId}/password`, { password: newPassword });
            enqueueSnackbar("Password updated", { variant: "success" });
            setNewPassword("");
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to update password", { variant: "error" });
        }
    };

    const handleSendResetEmail = async () => {
        try {
            await axios.post('/api/v1/password/forgot', { email });
            enqueueSnackbar(`Reset email sent to ${email}`, { variant: "success" });
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to send reset email", { variant: "error" });
        }
    };

    useEffect(() => {
        if (user && user._id !== userId) {
            dispatch(getUserDetails(userId));
        } else {
            setName(user.name);
            setEmail(user.email);
            setGender(user.gender || "");
            setRole(user.role);
            setHasAdminAccess(user.role === "admin" || user.role === "staff");
            setAdminRole(typeof user.adminRole === "object" ? (user.adminRole?._id || "") : (user.adminRole || ""));
            setAvatarPreview(user.avatar?.url || "");
            setEmailVerified(user.emailVerified !== false);
        }
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (updateError) {
            enqueueSnackbar(updateError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isUpdated) {
            enqueueSnackbar("User Updates Successfully", { variant: "success" });
            dispatch({ type: UPDATE_USER_RESET });
            dispatch({ type: REMOVE_USER_DETAILS });
            navigate("/admin/users");
        }
    }, [dispatch, error, userId, user, navigate, isUpdated, updateError, enqueueSnackbar]);

    return (
        <>
            <MetaData title="Admin: Update User | 1313 Mart" />

            {updateLoading && <BackdropLoader />}

            {loading ? <Loading /> : (
                <>
                    <div className="flex flex-col bg-white shadow-lg rounded-lg mx-auto w-lg max-w-xl">
                        <h2 className="text-center text-2xl font-medium mt-6 text-gray-800">Update Profile</h2>

                        <form
                            onSubmit={updateUserSubmitHandler}
                            className="p-5 sm:p-10"
                        >
                            <div className="flex flex-col gap-3 items-start">

                                {/* <!-- input container column --> */}
                                <div className="flex flex-col w-full justify-between sm:flex-col gap-3 items-center">
                                    <TextField
                                        fullWidth
                                        label="Full Name"
                                        name="name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                    />
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        type="email"
                                        name="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </div>
                                {/* <!-- input container column --> */}

                                {/* <!-- gender input --> */}
                                <div className="flex gap-4 items-center">
                                    <h2 className="text-md">Your Gender :</h2>
                                    <div className="flex items-center gap-6" id="radioInput">
                                        <RadioGroup
                                            row
                                            aria-labelledby="radio-buttons-group-label"
                                            name="radio-buttons-group"
                                        >
                                            <FormControlLabel name="gender" value="male" checked={gender === "male"} onChange={(e) => setGender(e.target.value)} control={<Radio required />} label="Male" />
                                            <FormControlLabel name="gender" value="female" checked={gender === "female"} onChange={(e) => setGender(e.target.value)} control={<Radio required />} label="Female" />
                                        </RadioGroup>
                                    </div>
                                </div>
                                {/* <!-- gender input --> */}

                                <div className="flex flex-col w-full justify-between sm:flex-row gap-3 items-center">
                                    <Avatar
                                        alt="Avatar Preview"
                                        src={avatarPreview}
                                        sx={{ width: 56, height: 56 }}
                                    />
                                </div>

                                <div className="w-full border-t pt-3">
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={hasAdminAccess}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    setHasAdminAccess(checked);
                                                    setRole(checked ? "staff" : "user");
                                                    if (!checked) setAdminRole("");
                                                }}
                                            />
                                        }
                                        label="Give this user access to the Admin Panel"
                                    />
                                    <p className="text-xs text-gray-400 -mt-1 mb-2">
                                        Leave unchecked for a regular customer account. Only accounts with this
                                        checked can ever log in to the admin panel.
                                    </p>
                                </div>

                                {hasAdminAccess && (
                                    <TextField
                                        label="Admin Panel Role"
                                        select
                                        fullWidth
                                        variant="outlined"
                                        required
                                        value={role}
                                        onChange={(e) => setRole(e.target.value)}
                                    >
                                        <MenuItem value={"staff"}>Staff (custom access)</MenuItem>
                                        <MenuItem value={"admin"}>Admin (full access)</MenuItem>
                                    </TextField>
                                )}

                                {hasAdminAccess && role === "staff" && (
                                    <TextField
                                        label="Staff Role"
                                        select
                                        fullWidth
                                        variant="outlined"
                                        required
                                        value={adminRole}
                                        onChange={(e) => setAdminRole(e.target.value)}
                                        helperText={allRoles.length === 0 ? "No roles created yet — go to Role Management first" : "Controls which admin pages this user can access"}
                                    >
                                        {allRoles.map((r) => (
                                            <MenuItem value={r._id} key={r._id}>{r.name}</MenuItem>
                                        ))}
                                    </TextField>
                                )}

                                <FormControlLabel
                                    control={<Checkbox checked={emailVerified} onChange={(e) => setEmailVerified(e.target.checked)} />}
                                    label="Email Verified (can log in with password immediately — no OTP required)"
                                />

                                <button type="submit" className="text-white py-3 w-full bg-primary-orange shadow hover:shadow-lg rounded-sm font-medium">Update</button>
                                <Link className="hover:bg-gray-100 text-primary-blue text-center py-3 w-full shadow border rounded-sm font-medium" to="/admin/users">Cancel</Link>

                                <div className="w-full border-t pt-4 mt-2 flex flex-col gap-3">
                                    <p className="font-medium text-gray-700">Password Tools</p>

                                    <TextField
                                        fullWidth
                                        label="Set New Password"
                                        type="password"
                                        size="small"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        helperText="Takes effect immediately — at least 8 characters"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleSetPassword}
                                        className="border border-primary-blue text-primary-blue py-2 rounded-sm font-medium hover:bg-primary-blue hover:text-white"
                                    >
                                        Set New Password
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleSendResetEmail}
                                        className="border border-primary-green text-primary-green py-2 rounded-sm font-medium hover:bg-primary-green hover:text-white"
                                    >
                                        Send Password Reset Email
                                    </button>
                                </div>
                            </div>

                        </form>

                    </div>
                </>
            )}
        </>
    );
};

export default UpdateUser;